import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, Lock, User, Phone, Eye, EyeOff, UserPlus, ArrowRight, Gift, Copy, Check, Sparkles, PawPrint } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { useAuth, formatApiErrorDetail } from '@/context/AuthContext';
import { toast } from 'sonner';

const Register = () => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get('redirect') || '/';
  const refFromUrl = (searchParams.get('ref') || '').toUpperCase();

  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    referral_code: refFromUrl,
    pet_name: '',
    pet_birthday: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [successOpen, setSuccessOpen] = useState(false);
  const [createdUser, setCreatedUser] = useState(null);
  const [copied, setCopied] = useState(false);

  // Keep referral field synced if URL param arrives later
  useEffect(() => {
    if (refFromUrl && !form.referral_code) {
      setForm((f) => ({ ...f, referral_code: refFromUrl }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refFromUrl]);

  const handleChange = (field) => (e) =>
    setForm({ ...form, [field]: field === 'referral_code' ? e.target.value.toUpperCase() : e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    setLoading(true);
    try {
      const user = await register(form);
      setCreatedUser(user);
      setSuccessOpen(true);
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleContinue = () => {
    setSuccessOpen(false);
    navigate(redirect);
  };

  const handleCopyReferral = async () => {
    if (!createdUser?.referral_code) return;
    try {
      await navigator.clipboard.writeText(createdUser.referral_code);
      setCopied(true);
      toast.success('Código copiado');
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      toast.error('No se pudo copiar');
    }
  };

  const shareUrl = createdUser?.referral_code
    ? `${window.location.origin}/registro?ref=${createdUser.referral_code}`
    : '';
  const whatsappHref = createdUser?.referral_code
    ? `https://wa.me/?text=${encodeURIComponent(
        `¡Hola! Te invito a Nutripet Gourmet — nutrición premium para tu mascota. Usa mi código ${createdUser.referral_code} al registrarte y ambos ganamos descuento: ${shareUrl}`
      )}`
    : '#';

  return (
    <div className="min-h-[80vh] bg-gradient-to-br from-[#F7F2E8] via-[#FDFBF7] to-[#F7F2E8] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-3xl border border-[#E7E5E4] p-8 shadow-sm">
          <div className="text-center mb-8">
            <div className="w-14 h-14 rounded-full bg-[#D47A4A] flex items-center justify-center mx-auto mb-4">
              <UserPlus className="w-6 h-6 text-white" />
            </div>
            <p className="text-xs tracking-[0.25em] uppercase text-[#D47A4A] font-semibold mb-2">Crear cuenta</p>
            <h1 className="font-display font-bold text-3xl text-[#1C1917] tracking-tight">Únete a la familia</h1>
            <p className="text-sm text-[#57534E] mt-2">
              Al registrarte recibes un <b>cupón del 10%</b> en tu correo 🎁 (compras desde $200.000)
            </p>
          </div>

          {refFromUrl && (
            <div
              className="mb-4 p-3 rounded-xl bg-[#F0F5EF] border border-[#2A5C43]/20 text-[#2A5C43] text-sm flex items-center gap-2"
              data-testid="register-referral-banner"
            >
              <Sparkles className="w-4 h-4 shrink-0" />
              <span>Te registras con el código <b>{refFromUrl}</b> — tu amigo/a también gana un cupón.</span>
            </div>
          )}

          {error && (
            <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="register-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name" className="text-[#1C1917] font-medium">Nombre completo</Label>
              <div className="relative mt-1">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8BA58F]" />
                <Input
                  id="name"
                  type="text"
                  required
                  value={form.name}
                  onChange={handleChange('name')}
                  className="pl-10 h-11 rounded-xl border-[#E7E5E4] focus-visible:ring-[#2A5C43]"
                  placeholder="Tu nombre"
                  data-testid="register-name-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email" className="text-[#1C1917] font-medium">Correo electrónico</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8BA58F]" />
                <Input
                  id="email"
                  type="email"
                  required
                  autoComplete="email"
                  value={form.email}
                  onChange={handleChange('email')}
                  className="pl-10 h-11 rounded-xl border-[#E7E5E4] focus-visible:ring-[#2A5C43]"
                  placeholder="tu@correo.com"
                  data-testid="register-email-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="phone" className="text-[#1C1917] font-medium">
                Teléfono <span className="text-[#8BA58F] font-normal">(opcional)</span>
              </Label>
              <div className="relative mt-1">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8BA58F]" />
                <Input
                  id="phone"
                  type="tel"
                  value={form.phone}
                  onChange={handleChange('phone')}
                  className="pl-10 h-11 rounded-xl border-[#E7E5E4] focus-visible:ring-[#2A5C43]"
                  placeholder="300 123 4567"
                  data-testid="register-phone-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-[#1C1917] font-medium">Contraseña</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8BA58F]" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  minLength={6}
                  autoComplete="new-password"
                  value={form.password}
                  onChange={handleChange('password')}
                  className="pl-10 pr-10 h-11 rounded-xl border-[#E7E5E4] focus-visible:ring-[#2A5C43]"
                  placeholder="Mínimo 6 caracteres"
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8BA58F] hover:text-[#2A5C43]"
                  data-testid="register-toggle-password"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="pet_name" className="text-[#1C1917] font-medium">
                  Tu mascota <span className="text-[#8BA58F] font-normal">(opcional)</span>
                </Label>
                <div className="relative mt-1">
                  <PawPrint className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8BA58F]" />
                  <Input
                    id="pet_name"
                    type="text"
                    value={form.pet_name}
                    onChange={handleChange('pet_name')}
                    className="pl-10 h-11 rounded-xl border-[#E7E5E4] focus-visible:ring-[#2A5C43]"
                    placeholder="Nombre"
                    data-testid="register-pet-name-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="pet_birthday" className="text-[#1C1917] font-medium">Su cumpleaños</Label>
                <Input
                  id="pet_birthday"
                  type="date"
                  value={form.pet_birthday}
                  onChange={handleChange('pet_birthday')}
                  max={new Date().toISOString().slice(0, 10)}
                  className="mt-1 h-11 rounded-xl border-[#E7E5E4] focus-visible:ring-[#2A5C43]"
                  data-testid="register-pet-birthday-input"
                />
              </div>
              <p className="col-span-2 -mt-1 text-xs text-[#8BA58F]">El día de su cumpleaños te enviamos un cupón sorpresa 🎂</p>
            </div>

            <div>
              <Label htmlFor="referral_code" className="text-[#1C1917] font-medium">
                Código amigo <span className="text-[#8BA58F] font-normal">(opcional)</span>
              </Label>
              <div className="relative mt-1">
                <Gift className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8BA58F]" />
                <Input
                  id="referral_code"
                  type="text"
                  value={form.referral_code}
                  onChange={handleChange('referral_code')}
                  className="pl-10 h-11 rounded-xl border-[#E7E5E4] focus-visible:ring-[#2A5C43] uppercase tracking-wide"
                  placeholder="AMIGO-XXXXXX"
                  data-testid="register-referral-input"
                />
              </div>
              <p className="mt-1 text-xs text-[#8BA58F]">Si te invitó un amigo, ambos ganan un cupón adicional 🎉</p>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#2A5C43] hover:bg-[#214A35] text-white rounded-full font-semibold"
              data-testid="register-submit"
            >
              {loading ? 'Creando cuenta...' : (
                <>Crear mi cuenta <ArrowRight className="w-4 h-4 ml-2" /></>
              )}
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t border-[#E7E5E4] text-center">
            <p className="text-sm text-[#57534E]">
              ¿Ya tienes cuenta?{' '}
              <Link to={`/login${redirect !== '/' ? `?redirect=${redirect}` : ''}`} className="text-[#2A5C43] font-semibold hover:underline" data-testid="register-login-link">
                Inicia sesión
              </Link>
            </p>
          </div>
        </div>

        <p className="text-center text-xs text-[#57534E] mt-6">
          Al registrarte aceptas nuestros <Link to="/terminos" className="underline">términos</Link> y <Link to="/privacidad" className="underline">política de privacidad</Link>
        </p>
      </div>

      {/* Success popup */}
      <Dialog open={successOpen} onOpenChange={(v) => { if (!v) handleContinue(); }}>
        <DialogContent className="max-w-md rounded-3xl p-0 overflow-hidden" data-testid="register-success-dialog">
          <div className="bg-gradient-to-br from-[#2A5C43] to-[#1a4030] text-white px-6 py-8 text-center">
            <div className="w-16 h-16 rounded-full bg-white/15 mx-auto flex items-center justify-center mb-3">
              <Mail className="w-8 h-8" />
            </div>
            <p className="text-[11px] tracking-[0.25em] uppercase opacity-80 mb-1">¡Cuenta creada!</p>
            <h2 className="font-display font-bold text-2xl leading-tight">Revisa tu correo 📬</h2>
            <p className="text-sm opacity-90 mt-2">
              Te enviamos tu <b>cupón de bienvenida 10%</b> a <b>{createdUser?.email}</b>.
              Válido en compras desde $200.000 y, al usarlo, recibes otro 5% para tu próxima compra.
            </p>
          </div>

          <div className="p-6">
            {createdUser?.referral_code && (
              <div className="rounded-2xl border-2 border-dashed border-[#D47A4A] bg-[#FFF7EE] p-4 text-center">
                <p className="text-[10px] tracking-[0.2em] uppercase text-[#8BA58F] mb-1">Tu código amigo</p>
                <p className="font-display font-bold text-2xl text-[#D47A4A] tracking-widest">
                  {createdUser.referral_code}
                </p>
                <p className="text-xs text-[#57534E] mt-2">
                  Compártelo. Cuando un amigo se registre, <b>ambos ganan un 5% extra</b>.
                </p>

                <div className="flex flex-col sm:flex-row gap-2 mt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCopyReferral}
                    className="flex-1 rounded-full border-[#D47A4A] text-[#D47A4A] hover:bg-[#D47A4A] hover:text-white"
                    data-testid="register-copy-referral"
                  >
                    {copied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                    {copied ? 'Copiado' : 'Copiar'}
                  </Button>
                  <a
                    href={whatsappHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center rounded-full bg-[#25D366] hover:bg-[#20b858] text-white h-10 px-4 text-sm font-semibold transition-colors"
                    data-testid="register-share-whatsapp"
                  >
                    Compartir en WhatsApp
                  </a>
                </div>
              </div>
            )}

            <Button
              onClick={handleContinue}
              className="w-full mt-5 h-12 bg-[#2A5C43] hover:bg-[#214A35] text-white rounded-full font-semibold"
              data-testid="register-continue-btn"
            >
              Continuar a la tienda <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <p className="text-[11px] text-[#8BA58F] text-center mt-3">
              ¿No ves el correo? Revisa la carpeta de spam.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Register;
